import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { apiClient } from "@/api/client";
import {
  parseApiResponse,
  wrappedListResponseSchema,
  wrappedResponseSchema,
} from "@/api/responseSchemas";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { useAuthStore } from "@/stores/useAuthStore";
import { z } from "zod";
import { captureEvent, type WrappedPeriodKind } from "@/analytics/posthog";

const WRAPPED_STALE_TIME = 60 * 60 * 1000;

const isNotFoundError = (error: unknown) =>
  typeof error === "object" &&
  error !== null &&
  "response" in error &&
  (error as AxiosError).response?.status === 404;

const fetchWrapped = async (period?: string) => {
  const endpoint = period === undefined ? "/wrapped/latest" : `/wrapped/${period}`;

  try {
    const response = await apiClient.get(endpoint);
    return parseApiResponse(wrappedResponseSchema, response.data, `GET ${endpoint}`);
  } catch (error) {
    if (isNotFoundError(error)) return null;
    throw error;
  }
};

export const useWrappedList = () => {
  const token = useAuthStore((state) => state.token);
  const userId = useAuthStore((state) => state.user?.id);

  return useQuery({
    queryKey: ["wrapped", userId, "list"],
    queryFn: async () => {
      const response = await apiClient.get("/wrapped/");
      return parseApiResponse(wrappedListResponseSchema, response.data, "GET /wrapped/");
    },
    enabled: FEATURE_FLAGS.wrapped && !!token,
    staleTime: WRAPPED_STALE_TIME,
  });
};

export const useWrapped = (period?: string) => {
  const token = useAuthStore((state) => state.token);
  const userId = useAuthStore((state) => state.user?.id);

  return useQuery({
    queryKey: ["wrapped", userId, period ?? "latest"],
    queryFn: () => fetchWrapped(period),
    enabled: FEATURE_FLAGS.wrapped && !!token,
    staleTime: WRAPPED_STALE_TIME,
    retry: (failureCount, error) => !isNotFoundError(error) && failureCount < 3,
  });
};

export const useRegisterWrappedView = (periodKind: WrappedPeriodKind = "latest") => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (wrappedId: number) => {
      const endpoint = `/wrapped/${wrappedId}/view`;
      const response = await apiClient.post(endpoint);
      return parseApiResponse(z.boolean(), response.data, `POST ${endpoint}`);
    },
    onSuccess: () => {
      captureEvent("wrapped_viewed", { period_kind: periodKind });
      return queryClient.invalidateQueries({ queryKey: ["wrapped"] });
    },
  });
};
